import React, { useState, useEffect } from 'react';
import { Palette, CheckCircle, RefreshCw, Eye, Code, Monitor, Smartphone, Tablet } from 'lucide-react';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';


const DesignSystemUpdate = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateComplete, setUpdateComplete] = useState(false);
  const [selectedView, setSelectedView] = useState('desktop');

  // Color palette as selected on July 6, 2025 at 10:00 PM
  const colorPalette = {
    primary: {
      name: 'Deep Burgundy',
      hex: '#8B1538',
      description: 'Main brand accent color',
      usage: 'Primary buttons, links, brand elements'
    },
    secondary: {
      name: 'Warm Gold',
      hex: '#D4AF37',
      description: 'Highlight and call-to-action color',
      usage: 'CTA buttons, highlights, premium badges'
    },
    success: {
      name: 'Rich Emerald',
      hex: '#50C878',
      description: 'Success states and confirmations',
      usage: 'Success messages, completed states'
    },
    notification: {
      name: 'Soft Coral',
      hex: '#FF7F7F',
      description: 'Notifications and alerts',
      usage: 'Notification badges, alert messages'
    },
    text: {
      name: 'Charcoal Gray',
      hex: '#36454F',
      description: 'Primary text color',
      usage: 'Body text, headings, labels'
    },
    background: {
      name: 'Light Gray',
      hex: '#F5F5F5',
      description: 'Background surfaces',
      usage: 'Page backgrounds, sections'
    },
    surface: {
      name: 'Pure White',
      hex: '#FFFFFF',
      description: 'Cards and containers',
      usage: 'Cards, modals, input fields'
    }
  };

  const handleUpdateColors = () => {
    setIsUpdating(true);
    // Simulate color update process
    setTimeout(() => {
      setIsUpdating(false);
      setUpdateComplete(true);
      // Apply the new color scheme to the document
      document.documentElement.style.setProperty('--primary-color', colorPalette.primary.hex);
      document.documentElement.style.setProperty('--secondary-color', colorPalette.secondary.hex);
      document.documentElement.style.setProperty('--success-color', colorPalette.success.hex);
      document.documentElement.style.setProperty('--notification-color', colorPalette.notification.hex);
      document.documentElement.style.setProperty('--text-color', colorPalette.text.hex);
      document.documentElement.style.setProperty('--background-color', colorPalette.background.hex);
      document.documentElement.style.setProperty('--surface-color', colorPalette.surface.hex);
    }, 2000);
  };

  const ColorCard = ({ color, colorKey }) => (
    <motion.div
      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div 
        className="h-24 w-full"
        style={{ backgroundColor: color.hex }}
      />
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-1" style={{ color: colorPalette.text.hex }}>
          {color.name}
        </h3>
        <p className="text-sm font-mono mb-2" style={{ color: colorPalette.text.hex }}>
          {color.hex}
        </p>
        <p className="text-sm mb-2" style={{ color: colorPalette.text.hex }}>
          {color.description}
        </p>
        <div className="text-xs px-2 py-1 rounded-full bg-gray-100 inline-block">
          {color.usage}
        </div>
      </div>
    </motion.div>
  );

  const PreviewComponent = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold mb-4" style={{ color: colorPalette.text.hex }}>
        {title}
      </h3>
      {children}
    </div>
  );

  const ViewportSelector = () => (
    <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
      {[
        { key: 'desktop', icon: Monitor, label: 'Desktop' },
        { key: 'tablet', icon: Tablet, label: 'Tablet' },
        { key: 'mobile', icon: Smartphone, label: 'Mobile' }
      ].map(({ key, icon: Icon, label }) => (
        <button
          key={key}
          onClick={() => setSelectedView(key)}
          className={`flex items-center px-4 py-2 rounded-md transition-all ${
            selectedView === key 
              ? 'bg-white shadow-sm' 
              : 'hover:bg-gray-200'
          }`}
          style={{ 
            color: selectedView === key ? colorPalette.primary.hex : colorPalette.text.hex 
          }}
        >
          <Icon className="w-4 h-4 mr-2" />
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: colorPalette.background.hex }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex justify-center items-center mb-4">
            <Palette className="w-12 h-12 mr-4" style={{ color: colorPalette.primary.hex }} />
            <h1 className="text-4xl font-bold" style={{ color: colorPalette.text.hex }}>
              Design System Update
            </h1>
          </div>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: colorPalette.text.hex }}>
            Global color scheme reverted to the original palette selected on July 6, 2025 at 10:00 PM.
            Maintaining cultural authenticity while ensuring WCAG AA accessibility standards.
          </p>
        </motion.div>

        {/* Update Status */}
        {updateComplete && (
          <motion.div
            className="bg-white rounded-lg shadow-md p-6 mb-8 border-l-4"
            style={{ borderLeftColor: colorPalette.success.hex }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center">
              <CheckCircle className="w-6 h-6 mr-3" style={{ color: colorPalette.success.hex }} />
              <div>
                <h3 className="font-semibold" style={{ color: colorPalette.text.hex }}>
                  Color Update Complete
                </h3>
                <p className="text-sm" style={{ color: colorPalette.text.hex }}>
                  All components have been successfully updated with the new color palette.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Color Palette Section */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold mb-6" style={{ color: colorPalette.text.hex }}>
            Color Palette - July 6, 2025 Selection
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {Object.entries(colorPalette).map(([key, color]) => (
              <ColorCard key={key} color={color} colorKey={key} />
            ))}
          </div>
          
          <div className="text-center">
            <button
              onClick={handleUpdateColors}
              disabled={isUpdating || updateComplete}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                updateComplete 
                  ? 'bg-gray-300 cursor-not-allowed' :'hover:shadow-lg transform hover:scale-105'
              }`}
              style={{ 
                backgroundColor: updateComplete ? '#e5e7eb' : colorPalette.primary.hex,
                color: '#FFFFFF'
              }}
            >
              {isUpdating ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin inline" />
                  Updating Colors...
                </>
              ) : updateComplete ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2 inline" />
                  Update Complete
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 inline" />
                  Apply Color Update
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Preview Section */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold mb-6" style={{ color: colorPalette.text.hex }}>
            Component Preview
          </h2>
          
          <ViewportSelector />

          <div className={`${selectedView === 'mobile' ? 'max-w-sm' : selectedView === 'tablet' ? 'max-w-md' : 'max-w-full'} mx-auto`}>
            {/* Button Examples */}
            <PreviewComponent title="Buttons">
              <div className="flex flex-wrap gap-4">
                <button
                  className="px-6 py-2 rounded-lg font-semibold text-white transition-all hover:shadow-lg"
                  style={{ backgroundColor: colorPalette.primary.hex }}
                >
                  Primary Button
                </button>
                <button
                  className="px-6 py-2 rounded-lg font-semibold text-white transition-all hover:shadow-lg"
                  style={{ backgroundColor: colorPalette.secondary.hex }}
                >
                  Secondary Button
                </button>
                <button
                  className="px-6 py-2 rounded-lg font-semibold text-white transition-all hover:shadow-lg"
                  style={{ backgroundColor: colorPalette.success.hex }}
                >
                  Success Button
                </button>
              </div>
            </PreviewComponent>

            {/* Cards Example */}
            <PreviewComponent title="Cards">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="font-semibold mb-2" style={{ color: colorPalette.text.hex }}>
                    Vendor Profile Card
                  </h3>
                  <p className="text-sm mb-4" style={{ color: colorPalette.text.hex }}>
                    This card showcases how vendor information will appear with the new color scheme.
                  </p>
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: colorPalette.success.hex }}
                    />
                    <span className="text-sm font-medium" style={{ color: colorPalette.text.hex }}>
                      Verified Vendor
                    </span>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="font-semibold mb-2" style={{ color: colorPalette.text.hex }}>
                    Notification Example
                  </h3>
                  <div 
                    className="p-3 rounded-lg mb-4"
                    style={{ backgroundColor: `${colorPalette.notification.hex}20` }}
                  >
                    <div className="flex items-center">
                      <div 
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: colorPalette.notification.hex }}
                      />
                      <span className="text-sm" style={{ color: colorPalette.text.hex }}>
                        New message received
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </PreviewComponent>

            {/* Typography Example */}
            <PreviewComponent title="Typography">
              <div className="space-y-4">
                <h1 className="text-3xl font-bold" style={{ color: colorPalette.text.hex }}>
                  Heading 1 - Main Title
                </h1>
                <h2 className="text-2xl font-semibold" style={{ color: colorPalette.text.hex }}>
                  Heading 2 - Section Title
                </h2>
                <h3 className="text-xl font-medium" style={{ color: colorPalette.text.hex }}>
                  Heading 3 - Subsection
                </h3>
                <p className="text-base" style={{ color: colorPalette.text.hex }}>
                  This is regular body text that demonstrates how the new charcoal gray color improves readability while maintaining the South Asian cultural aesthetic.
                </p>
                <p className="text-sm opacity-75" style={{ color: colorPalette.text.hex }}>
                  This is smaller text for captions and secondary information.
                </p>
              </div>
            </PreviewComponent>
          </div>
        </motion.div>

        {/* Technical Details */}
        <motion.div
          className="bg-white rounded-lg shadow-md p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h2 className="text-2xl font-bold mb-6" style={{ color: colorPalette.text.hex }}>
            Technical Implementation
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-3 flex items-center" style={{ color: colorPalette.text.hex }}>
                <Code className="w-5 h-5 mr-2" />
                Color Tokens
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 text-sm font-mono">
                <div className="space-y-1">
                  <div>--primary: {colorPalette.primary.hex}</div>
                  <div>--secondary: {colorPalette.secondary.hex}</div>
                  <div>--success: {colorPalette.success.hex}</div>
                  <div>--notification: {colorPalette.notification.hex}</div>
                  <div>--text: {colorPalette.text.hex}</div>
                  <div>--background: {colorPalette.background.hex}</div>
                  <div>--surface: {colorPalette.surface.hex}</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-3 flex items-center" style={{ color: colorPalette.text.hex }}>
                <Eye className="w-5 h-5 mr-2" />
                Accessibility
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" style={{ color: colorPalette.success.hex }} />
                  <span style={{ color: colorPalette.text.hex }}>WCAG AA Compliant</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" style={{ color: colorPalette.success.hex }} />
                  <span style={{ color: colorPalette.text.hex }}>High Contrast Ratios</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" style={{ color: colorPalette.success.hex }} />
                  <span style={{ color: colorPalette.text.hex }}>Color Blind Friendly</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" style={{ color: colorPalette.success.hex }} />
                  <span style={{ color: colorPalette.text.hex }}>Cultural Authenticity</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DesignSystemUpdate;